/**
 * Константы мастера онбординга (CTHS-216/217).
 *
 * Набор и порядок шагов задаёт backend (`GET /api/onboarding/steps`) — здесь только запасной
 * список на случай, когда контракт шагов не доехал (сеть, старый backend), и справочники
 * вариантов для шагов, у которых своего справочника в продукте нет.
 */
import {OnboardingStepMeta} from '../../support/onboardingApi';

/** Коды шагов мастера: по ним выбирается экран (значения enum `OnboardingStep` на backend). */
export const STEP_CODES = {
    welcome: 'WELCOME',
    strategy: 'STRATEGY',
    profile: 'PROFILE',
    sizes: 'SIZES',
    style: 'STYLE',
    activities: 'ACTIVITIES',
    consents: 'CONSENTS',
    location: 'LOCATION',
    aiMemory: 'AI_MEMORY',
    firstItem: 'FIRST_ITEM',
    channels: 'CHANNELS',
    done: 'DONE',
} as const;

/**
 * Запасной контракт шагов — копия активных слотов backend. Используется, только если
 * `GET /api/onboarding/steps` недоступен: номера слотов должны совпадать с backend, иначе
 * ответы лягут не в тот ключ.
 */
export const FALLBACK_STEPS: OnboardingStepMeta[] = [
    {number: 1, code: STEP_CODES.welcome, order: 1, skippable: false},
    {number: 3, code: STEP_CODES.strategy, order: 2, skippable: true},
    {number: 4, code: STEP_CODES.profile, order: 3, skippable: true},
    {number: 5, code: STEP_CODES.sizes, order: 4, skippable: true},
    {number: 6, code: STEP_CODES.style, order: 5, skippable: true},
    {number: 8, code: STEP_CODES.activities, order: 6, skippable: true},
    {number: 9, code: STEP_CODES.consents, order: 7, skippable: true},
    {number: 10, code: STEP_CODES.location, order: 8, skippable: true},
    {number: 11, code: STEP_CODES.aiMemory, order: 9, skippable: true},
    {number: 12, code: STEP_CODES.firstItem, order: 10, skippable: true},
    {number: 13, code: STEP_CODES.channels, order: 11, skippable: true},
    {number: 14, code: STEP_CODES.done, order: 12, skippable: false},
];

/** Заголовок и подзаголовок шага: один источник для экрана и для тестов. */
export const STEP_TITLES: Record<string, {title: string; subtitle: string}> = {
    [STEP_CODES.welcome]: {
        title: 'Настроим вашего стилиста',
        subtitle: 'Несколько коротких шагов — и система будет вести гардероб под вас. Любой шаг можно пропустить.',
    },
    [STEP_CODES.strategy]: {
        title: 'Как вести гардероб',
        subtitle: 'Стратегия задаёт правила: что хранить, что предлагать к выбытию и насколько самостоятельно действовать.',
    },
    [STEP_CODES.profile]: {
        title: 'Немного о вас',
        subtitle: 'Нужно, чтобы советы по посадке и образам были уместными. Все поля необязательны.',
    },
    [STEP_CODES.sizes]: {
        title: 'Ваши размеры',
        subtitle: 'Пригодятся при разборе гардероба и анализе покупок.',
    },
    [STEP_CODES.style]: {
        title: 'Ваш стиль',
        subtitle: 'Выберите стили, в которых вам комфортно, и опишите, к чему хотите прийти.',
    },
    [STEP_CODES.activities]: {
        title: 'Чем занимаетесь',
        subtitle: 'По этим сценариям стилист собирает образы и оценивает, чего в гардеробе не хватает.',
    },
    [STEP_CODES.consents]: {
        title: 'Согласия',
        subtitle: 'AI-функции работают только с вашего явного согласия. Отозвать можно в любой момент в настройках приватности.',
    },
    [STEP_CODES.location]: {
        title: 'Город и погода',
        subtitle: 'Образ дня учитывает прогноз. Город можно указать без координат — тогда погода не подключится.',
    },
    [STEP_CODES.aiMemory]: {
        title: 'Что важно знать стилисту',
        subtitle: 'Свободный текст: любимые цвета, ограничения, нелюбимые вещи, дресс-код.',
    },
    [STEP_CODES.firstItem]: {
        title: 'Первые вещи',
        subtitle: 'Загрузите несколько фото — система распознает вещи и заполнит карточки сама.',
    },
    [STEP_CODES.channels]: {
        title: 'Уведомления',
        subtitle: 'Telegram присылает напоминания и образ дня. Привязка не обязательна.',
    },
    [STEP_CODES.done]: {
        title: 'Стилист готов',
        subtitle: 'Настройки сохранены. Их всегда можно изменить в профиле и на странице стратегии.',
    },
};

/** Активности пользователя: своего справочника в продукте нет, список фиксирован здесь. */
export const ACTIVITY_OPTIONS: {code: string; label: string}[] = [
    {code: 'WORK', label: 'Работа и офис'},
    {code: 'SPORT', label: 'Спорт и тренировки'},
    {code: 'TRAVEL', label: 'Путешествия'},
    {code: 'EVENTS', label: 'Выходы и мероприятия'},
    {code: 'STUDY', label: 'Учёба'},
    {code: 'HOME', label: 'Дом и отдых'},
    {code: 'OUTDOOR', label: 'Прогулки и природа'},
    {code: 'FAMILY', label: 'Дети и семейные дела'},
];

/** Пол — те же значения, что в настройках профиля (`UserPreferencesSection`). */
export const GENDER_OPTIONS: {value: string; label: string}[] = [
    {value: '', label: 'Не указан'},
    {value: 'MALE', label: 'Мужской'},
    {value: 'FEMALE', label: 'Женский'},
    {value: 'NON_BINARY', label: 'Небинарный'},
    {value: 'PREFER_NOT_TO_SAY', label: 'Предпочитаю не указывать'},
];

/** Максимальная длина свободного контекста для стилиста. */
export const AI_MEMORY_MAX = 500;

/** Сколько фото мастер принимает на шаге «первые вещи». */
export const FIRST_ITEM_MAX_PHOTOS = 5;

/** Общие классы карточки выбора (паттерн `StrategyPage`), чтобы шаги не расходились. */
export const choiceCardClass = (selected: boolean): string =>
    `min-h-[44px] px-4 py-3 rounded-xl border text-sm text-left transition-colors ${
        selected
            ? 'bg-brand-600 text-white border-brand-600'
            : 'bg-white dark:bg-ink-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-ink-700 hover:border-brand-500'
    }`;
